import React, { ComponentType } from "react"
import Markdown from "markdown-to-jsx"
import { components } from "../components/views/DefaultMarkdown"
import { LinkModalContextProvider, LinkToModal, ModalFromLink } from "components/views/LinkModal"
import { ArrowLink, Divider, FlexContainer, H1, H2, H3, H4, Text } from "@carrotfertility/carotene"
import { CalloutBox } from "components/views/CalloutBox"
import { ZendeskArrowLink } from "../components/views/atoms/links/ZendeskArrowLink"
import { isZendeskLink } from "carrot-api/HelpCenterClient"
import { createErrorBoundary } from "./createErrorBoundary"
import { reportErrorMessage } from "./ErrorReporting"
import { contentValidator } from "components/dynamic-content/util/contentValidation"

const EmptyElement = (): JSX.Element => {
  return <></>
}

const MarkdownErrorBoundary = createErrorBoundary(EmptyElement as ComponentType)

const elements = {
  h1({ id, children }) {
    return <H1 id={id}>{children}</H1>
  },
  h2({ id, children }) {
    return <H2 id={id}>{children}</H2>
  },
  h3({ id, children }) {
    return <H3 id={id}>{children}</H3>
  },
  h4({ id, children }) {
    return <H4 id={id}>{children}</H4>
  },
  p({ children }) {
    return (
      <Text as="p" color="black80">
        {children}
      </Text>
    )
  },
  a({ href, title, target = "_blank", children }) {
    if (!href) {
      reportErrorMessage(`Empty href found in Markdown with title ${title} and children ${JSON.stringify(children)}`)
    }
    if (isZendeskLink(href)) {
      return (
        <ZendeskArrowLink target={target} title={title} to={href} className="markdown-textlink" color="black80">
          {children}
        </ZendeskArrowLink>
      )
    }
    return (
      <ArrowLink target={target} title={title} to={href} className="markdown-textlink" size="medium" color="black80">
        {children}
      </ArrowLink>
    )
  },
  ol({ children }) {
    return (
      <ol style={{ listStyle: "decimal" }}>
        <FlexContainer direction="column" gap="tiny" paddingLeftRight="small">
          {children}
        </FlexContainer>
      </ol>
    )
  },
  ul({ children }) {
    return (
      <ul>
        <FlexContainer direction="column" gap="tiny" paddingLeftRight="small">
          {children}
        </FlexContainer>
      </ul>
    )
  },
  li({ children }) {
    return (
      <li>
        <Text as="span" color="black-80" style={{ wordBreak: "break-word" }}>
          {children}
        </Text>
      </li>
    )
  },
  table({ children }) {
    return (
      <table style={{ borderCollapse: "collapse", width: "100%", textAlign: "left", margin: "48px 0px" }}>
        {children}
      </table>
    )
  },
  th({ children }) {
    return (
      <th style={{ padding: "16px", border: "1px solid #ccc" }}>
        <Text weight="semibold">{children}</Text>
      </th>
    )
  },
  td({ children }) {
    return (
      <td style={{ padding: "16px", border: "1px solid #ccc" }}>
        <Text>{children}</Text>
      </td>
    )
  },
  blockquote({ children }) {
    return <blockquote>{children}</blockquote>
  },
  strong({ children }) {
    return <Text weight="bold">{children}</Text>
  },
  em({ children }) {
    return <Text fontStyle="italic">{children}</Text>
  },
  hr() {
    return <Divider color="black10" />
  }
}

const CarrotMarkdown = ({
  source,
  styleOverrides = {},
  wrapper = "div"
}: {
  source: string
  styleOverrides?: any
  wrapper?: React.ElementType | null
}): JSX.Element => {
  const validateContentSource = (source) => {
    const customMarkdownTags = Object.keys({ ...styleOverrides, ...components }).filter(
      (markdownKey) => markdownKey.charAt(0) === markdownKey.charAt(0).toUpperCase()
    )
    return contentValidator(source, customMarkdownTags)
  }
  source = validateContentSource(source)
  const render = (
    <Markdown
      options={{
        overrides: {
          ...elements,
          ...components,
          LinkToModal(props) {
            return <LinkToModal {...props} />
          },
          ModalFromLink(props) {
            return <ModalFromLink {...props} />
          },
          CalloutBox(props) {
            return <CalloutBox {...props} />
          },
          Emptybr() {
            return <div />
          },
          ...styleOverrides
        },
        forceBlock: true,
        wrapper
      }}
    >
      {source}
    </Markdown>
  )
  return (
    <MarkdownErrorBoundary>
      <LinkModalContextProvider>{render}</LinkModalContextProvider>
    </MarkdownErrorBoundary>
  )
}

export default CarrotMarkdown
